import type {EntryPoint} from 'react-relay'
import type {LoaderFunctionArgs} from 'react-router'

import {BuildPageTabNamesEnum, toBuildId, toBuildTypeId} from '../../../../../types'
import type {ThinNestedEntryPointParamsObject} from '../../../../../types/entryPoints'
import {isPipelineOverviewEnabled} from '../../../../../utils/featureToggles'
import {makeResource} from '../../../../../utils/makeResource'
import {queryToObject} from '../../../../../utils/queryParams'
import {SELECTED_BUILD_DEPENDENCY_ID_PARAM} from '../../../../packages/BuildLog/FullBuildLog/BuildLogBuildNavigationWrapper/BuildLogBuildNavigationWrapper.consts'
import {BuildArtifactsTabEntryPoint} from '../../../BuildPage/BuildArtifactsTab/BuildArtifactsTab.entryPoint'
import {getBuildTabsEntryPoints} from '../../../BuildPage/BuildPage.utils'
import {BuildParametersTabEntryPoint} from '../../../BuildPage/BuildParametersTab/BuildParametersTab.entryPoint'
import {BuildTestsTabEntryPoint} from '../../../BuildPage/BuildTestsTab/BuildTestsTab.entryPoint'
import {DependenciesTabEntryPoint} from '../../../BuildPage/DependenciesTab/DependenciesTab.entryPoint'
import {PipelineRunTabs} from '../PipelineRunPage.consts'
import {ChangesTabEntryPoint} from '../PipelineTab/Details/JobDetails/ChangesTab/ChangesTab.entryPoint'
import {supportedTabs} from '../PipelineTab/Details/JobDetails/JobDetails.consts'
import {OverviewTabEntryPoint} from '../PipelineTab/Details/JobDetails/OverviewTab/OverviewTab.entryPoint'

import type {NestedEntryPoints, PipelineRunContentComponent} from './PipelineRunContent'

export const PipelineRunContentEntryPoint: EntryPoint<
  PipelineRunContentComponent,
  LoaderFunctionArgs
> = {
  root: makeResource(
    'PipelineRunContent',
    () => import(/* webpackChunkName: "PipelineRunContent" */ './PipelineRunContent'),
  ),
  getPreloadProps: arg => {
    const {request, params} = arg
    const url = new URL(request.url)
    const {
      job,
      tab,
      pipelineRunTab,
      [SELECTED_BUILD_DEPENDENCY_ID_PARAM]: pageFullLogBuildId,
    } = queryToObject(url.search)

    if (pipelineRunTab === PipelineRunTabs.CHAIN && params.pipelineId != null) {
      const buildTypeId = toBuildTypeId(params.pipelineId)
      const runId = toBuildId(params.pipelineRunId ?? '')
      const locator = `id:${runId}`
      return {
        entryPoints: {
          dependenciesTab: {
            entryPoint: DependenciesTabEntryPoint,
            entryPointParams: {buildTypeId, buildLocator: locator},
          },
        },
      }
    }

    if (job != null) {
      const entryPoints: ThinNestedEntryPointParamsObject<NestedEntryPoints> = {}
      const idParam = Number(pageFullLogBuildId ?? job)
      const id = Number.isNaN(idParam) ? Number(job) : idParam
      const buildLocator = `id:${id}`
      const isKnownTab = supportedTabs.some(tabId => tabId === tab)
      if (tab == null || tab === BuildPageTabNamesEnum.OVERVIEW || !isKnownTab) {
        entryPoints.pipelineOverviewTab = {
          entryPoint: OverviewTabEntryPoint,
          entryPointParams: buildLocator,
        }
      }

      switch (tab) {
        case BuildPageTabNamesEnum.TESTS:
          entryPoints.testsTab = {
            entryPoint: BuildTestsTabEntryPoint,
            entryPointParams: {
              ...arg,
              context: {
                buildLocator,
              },
            },
          }
          break
        case BuildPageTabNamesEnum.ARTIFACTS:
          entryPoints.artifactsTab = {
            entryPoint: BuildArtifactsTabEntryPoint,
            entryPointParams: buildLocator,
          }
          break
        case BuildPageTabNamesEnum.CHANGES:
          entryPoints.pipelineChangesTab = {
            entryPoint: ChangesTabEntryPoint,
            entryPointParams: id,
          }
          break
        case BuildPageTabNamesEnum.PARAMETERS:
          if (isPipelineOverviewEnabled) {
            entryPoints.parametersTab = {
              entryPoint: BuildParametersTabEntryPoint,
              entryPointParams: {buildLocator},
            }
          }
          break
        default:
      }

      return {
        entryPoints,
      }
    } else {
      const id = toBuildId(params.pipelineRunId ?? '')
      const buildLocator = `id:${id}`
      return {
        entryPoints: getBuildTabsEntryPoints({arg, buildLocator, buildTab: tab, isPipeline: true}),
      }
    }
  },
}
